import { useState } from 'react'
import ButtonEl from '../helpers/ctas/Button'
import themeConfig from '../../../theme.config'
import { dataLayerPush } from '../../helpers/thirdparty'

/**
 * useSearchForm — shareable hook that owns search term state and the submit
 * redirect logic. Use this in any component that embeds a <SearchForm />.
 */
export function useSearchForm() {
  const [searchTerm, setSearchTerm] = useState('')

  const submitForm = (evt) => {
    evt.preventDefault()
    const term = searchTerm.trim()
    if (term) {
      dataLayerPush({
        event: 'onsite_search',
        search_term: term,
      })
    }
    const loc = window.location
    const searchUrl = new URL(`${loc.origin}/${themeConfig.settings.searchPageStub}`)
    if (term) {
      searchUrl.searchParams.append('q', encodeURIComponent(term))
    }
    window.location = searchUrl
  }

  return { searchTerm, setSearchTerm, submitForm }
}

/**
 * SearchForm — pure presentational search form.
 *
 * Props:
 *  searchTerm      — controlled input value
 *  setSearchTerm   — setter for searchTerm
 *  onSubmit        — form submit handler (use submitForm from useSearchForm)
 *  isActive        — controls tabIndex / aria-hidden for keyboard/a11y
 *  formId          — id applied to the <form> element (must be unique per page)
 *  placeholder     — input placeholder text
 *  formClassName   — extra classes applied to the <form> element
 *  innerClassName  — extra classes applied to the inner flex wrapper div
 *  buttonTheme     — ButtonEl theme prop (defaults to 'secondary')
 *  buttonClassName — extra classes forwarded to the submit ButtonEl
 */
function SearchForm({
  searchTerm,
  setSearchTerm,
  onSubmit,
  isActive = true,
  formId = 'search-form',
  placeholder = 'Search term',
  formClassName = '',
  innerClassName = '',
  buttonTheme = 'secondary',
  buttonClassName = '',
}) {
  return (
    <form
      className={formClassName}
      onSubmit={onSubmit}
      role="search"
      aria-label="Sitewide"
      id={formId}
    >
      <div className={`w-full flex flex-nowrap items-center justify-center gap-4 ${innerClassName}`}>
        <input
          type="text"
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          tabIndex={isActive ? 0 : -1}
          aria-hidden={isActive ? 'false' : 'true'}
          aria-label="Site Search"
          className="rounded-md py-3 px-5 basis-auto grow min-w-0 bg-white text-(--sb-search-input-text-color) placeholder:text-(--sb-search-input-text-color) border border-(--sb-search-input-border) focus:outline-none focus:ring-2 focus:ring-(--sb-search-input-border)"
          value={searchTerm}
        />
        <ButtonEl
          active={isActive ? true : false}
          theme={buttonTheme}
          ariaHidden={isActive ? 'false' : 'true'}
          tabIndex={isActive ? 0 : -1}
          type="submit"
          className={`shrink-0 bg-(--sb-search-box-button) ${buttonClassName}`}
        >
          <span>Search</span>
        </ButtonEl>
      </div>
    </form>
  )
}

export default SearchForm
